import { captureMessage } from '@sentry/react';
import loadImage from 'blueimp-load-image';

import { SrcsetArray } from '@rover/kibble/core/Image/Image';
import File from '@rover/react-lib/src/utils/File';
import type { DropzoneFile } from '@rover/types/src/ImagePicker';

import type { FileTypes } from './File';

type ComboFiles = DropzoneFile | FileTypes;

type ImageSize = {
  width: string;
  height: string;
};

export function buildImageUrl(
  originalImage: string,
  width: string,
  height: string,
  fit: string = 'cover'
): string {
  try {
    const url = new URL(originalImage);
    url.searchParams.set('width', width);
    url.searchParams.set('height', height);
    url.searchParams.set('fit', fit);
    return url.toString();
  } catch (e) {
    return originalImage;
  }
}

export function buildSrcset(
  originalImage: string,
  sizes: ImageSize[],
  fit: string = 'cover'
): SrcsetArray {
  const urls = sizes.map((size) => buildImageUrl(originalImage, size.width, size.height, fit));
  return urls as SrcsetArray;
}

// eslint-disable-next-line consistent-return
export function dataURItoFile(dataURI: string): File | undefined {
  console.log('dataURI: ', dataURI);
  try {
    // convert base64 to raw binary data held in a string
    const byteString = atob(dataURI.split(',')[1]);
    console.log('byteString:', byteString);

    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    console.log('mimeString:', mimeString);

    // write the bytes of the string to an ArrayBuffer
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i += 1) {
      ia[i] = byteString.charCodeAt(i);
    }

    // create file from arraybuffer
    const dataView = new DataView(arrayBuffer);
    return new File([dataView.buffer], 'image-from-string', {
      type: mimeString,
    });
  } catch (e) {
    // @ts-expect-error
    if (e.name === 'InvalidCharacterError') {
      captureMessage(`Could not convert using atob the binary data: ${dataURI}.`);
    }
  }
}

export async function getImageFileDimensions(imageFile: ComboFiles): Promise<{
  height: number;
  width: number;
}> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();

    // gross old async pattern
    fr.onload = () => {
      const img = new Image();

      img.onload = () => {
        if (img.height && img.width) {
          resolve({
            height: img.height,
            width: img.width,
          });
        } else {
          reject(new Error('Image height and width not found.'));
        }
      };

      img.onerror = (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        const message = typeof err === 'string' ? err : 'Error during FileReader Image loading';
        reject(new Error(message));
      };

      if (typeof fr.result === 'string') {
        console.log('Image file type:', fr.result.substring(0, 30));
        console.log('Image src length: ', fr.result.length);
        img.src = fr.result;
      } else {
        reject(new Error('Image incorrectly parsed from file'));
      }
    };

    // read file
    fr.readAsDataURL(imageFile);
  });
}

const mimeTypeRegex = /data:(.*?);/;
export function mimeTypeFromDataUrl(b64string: string): string | null | undefined {
  const res = mimeTypeRegex.exec(b64string);
  return res && res[1];
}

export async function orientUploadedImageFile(
  imageFile: FileTypes
): Promise<FileTypes | undefined> {
  return new Promise((resolve, reject) => {
    loadImage(
      imageFile,
      async (canvas) => {
        // @ts-expect-error
        if (canvas.type === 'error') {
          reject(new Error('Failed to orient Image'));
        }
        if (canvas instanceof HTMLCanvasElement) {
          const dataUrl = canvas.toDataURL(imageFile.type);
          if (!dataUrl || dataUrl === 'data:,') {
            captureMessage(
              `file with name ${imageFile.name}, extension ${imageFile.type}, size ${imageFile.size}
               could not be loaded into canvas when trying to canvas.toDataUrl()`
            );
            reject(new Error('Failed to orient Image'));
          } else {
            resolve(dataURItoFile(canvas.toDataURL(imageFile.type)));
          }
        }
      },
      {
        orientation: 1,
        maxWidth: 4096,
        maxHeight: 4096,
      }
    );
  });
}

const image = {
  getImageFileDimensions,
  orientUploadedImageFile,
};

export default image;
